import { Product } from '../types/product';

interface GalwayCrystalRawProduct {
  name: string;
  price: string;
  sku: string;
  vendor: string;
  type: string;
  url: string;
  image: string;
  product_id: string;
  variant_id: string;
  collection: string;
  category: string;
  page: number;
  description: string;
  customizable: boolean;
  customization_fields: string;
  local_image: string;
}

class GalwayCrystalService {
  private galwayProducts: Product[] = [];
  private loaded = false;

  /**
   * Load Galway Crystal products from JSON file
   */
  async loadGalwayCrystalProducts(): Promise<void> {
    if (this.loaded) {
      return;
    }

    try {
      const response = await fetch('/Galway_crystal/galway_crystal_products.json');
      if (!response.ok) {
        throw new Error('Failed to load Galway Crystal products');
      }

      const rawProducts: GalwayCrystalRawProduct[] = await response.json();

      this.galwayProducts = rawProducts.map((raw) => this.convertToProduct(raw));
      this.loaded = true;

      console.log(`GalwayCrystalService: Loaded ${this.galwayProducts.length} products`);
    } catch (error) {
      console.error('Error loading Galway Crystal products:', error);
      throw error;
    }
  }

  /**
   * Convert Galway Crystal raw product to standard Product interface
   */
  private convertToProduct(raw: GalwayCrystalRawProduct): Product {
    // Extract numeric price from formatted string (€29.95 -> 29.95)
    const priceMatch = raw.price.match(/[\d.]+/);
    const price = priceMatch ? parseFloat(priceMatch[0]) : null;

    return {
      id: raw.sku.toLowerCase(),
      productId: raw.sku,
      name: raw.name,
      description: raw.description || '',
      price: price,
      priceFormatted: raw.price,
      size: '',
      category: raw.category,
      images: raw.local_image ? [`/${raw.local_image}`] : [],
      stockQuantity: 100,
      sku: raw.sku,
      page: raw.page,
      inStock: true,
      imagePath: raw.local_image,

      // Galway Crystal specific fields
      vendor: raw.vendor,
      type: raw.type, // Subcategory
      customizable: raw.customizable,
      customizationFields: raw.customization_fields,
      url: raw.url,
      localImage: raw.local_image,
      productIdExternal: raw.product_id,
    };
  }

  /**
   * Get all Galway Crystal products
   */
  getGalwayProducts(): Product[] {
    return this.galwayProducts;
  }

  /**
   * Search Galway Crystal products
   */
  searchGalwayProducts(query: string): Product[] {
    const lowerQuery = query.toLowerCase();

    return this.galwayProducts.filter((product) => {
      return (
        product.name.toLowerCase().includes(lowerQuery) ||
        product.description.toLowerCase().includes(lowerQuery) ||
        product.category.toLowerCase().includes(lowerQuery) ||
        (product.sku && product.sku.toLowerCase().includes(lowerQuery)) ||
        (product.type && product.type.toLowerCase().includes(lowerQuery))
      );
    });
  }

  /**
   * Filter Galway Crystal products by category or subcategory
   */
  filterGalwayByCategory(category: string): Product[] {
    return this.galwayProducts.filter(
      (p) =>
        p.category.toLowerCase() === category.toLowerCase() ||
        (p.type && p.type.toLowerCase() === category.toLowerCase())
    );
  }

  /**
   * Get Galway Crystal categories (main + subcategories)
   */
  getGalwayCategories(): string[] {
    // Fixed main categories in specific order
    const mainCategories = ['For Him', 'For Her', 'Glassware Bar'];

    // Extract subcategories from 'type' field
    const subcategories = Array.from(
      new Set(
        this.galwayProducts
          .map((p) => p.type)
          .filter((type): type is string => type !== undefined && type.trim() !== '')
      )
    );

    // Sort subcategories alphabetically
    subcategories.sort();

    return ['All', ...mainCategories, ...subcategories];
  }

  /**
   * Get product by ID
   */
  getProductById(id: string): Product | undefined {
    return this.galwayProducts.find((p) => p.id === id || p.sku === id);
  }

  /**
   * Reset service (for testing or switching collections)
   */
  reset(): void {
    this.galwayProducts = [];
    this.loaded = false;
  }
}

export const galwayCrystalService = new GalwayCrystalService();
